import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AccountMenuAction, MOBILE_LAYOUT_MEDIA_QUERY } from '@shared/constants';

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  private errorMessage = signal<string | null>(null);
  private titleService = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  isCoBrowseDialogVisible = signal(false);
  pageTitle = signal(this.titleService.getTitle());

  /** Whether the viewport uses the mobile layout (full-width content, drawer nav). */
  isMobileLayout = signal(false);

  /** Whether the mobile navigation drawer is open. */
  isMobileNavOpen = signal(false);

  /**
   * Action from the mobile nav footer (switch company or change password).
   * The top bar reads and clears this so the drawer can close before the account UI opens.
   */
  mobileAccountMenuAction = signal<AccountMenuAction | null>(null);

  constructor() {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      const title = this.getRouteTitle(this.route);
      if (title) {
        this.pageTitle.set(title);
      }
    });

    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mobileMediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY);
    this.isMobileLayout.set(mobileMediaQuery.matches);

    mobileMediaQuery.addEventListener('change', (event) => {
      this.isMobileLayout.set(event.matches);

      if (!event.matches) {
        this.closeMobileNav();
      }
    });
  }

  toggleMobileNav(): void {
    this.isMobileNavOpen.update((open) => !open);
  }

  openMobileNav(): void {
    this.isMobileNavOpen.set(true);
  }

  closeMobileNav(): void {
    this.isMobileNavOpen.set(false);
  }

  requestMobileAccountSwitch(): void {
    this.mobileAccountMenuAction.set(AccountMenuAction.Switch);
  }

  requestMobileChangePassword(): void {
    this.mobileAccountMenuAction.set(AccountMenuAction.Password);
  }

  /** Reads and clears a pending mobile nav footer account action. */
  consumeMobileAccountMenuAction(): AccountMenuAction | null {
    const action = this.mobileAccountMenuAction();
    this.mobileAccountMenuAction.set(null);
    return action;
  }

  private getRouteTitle(route: ActivatedRoute): string | null {
    let deepestRoute = route;
    while (deepestRoute.firstChild) {
      deepestRoute = deepestRoute.firstChild;
    }

    // Access top-level `title` property and check if it's a string
    const title = deepestRoute.snapshot.routeConfig?.title;
    return typeof title === 'string' ? title : null;
  }

  showCoBrowseDialog() {
    this.isCoBrowseDialogVisible.set(true);
  }

  hideCoBrowseDialog() {
    this.isCoBrowseDialogVisible.set(false);
  }

  hasError() {
    return this.errorMessage() !== null && this.errorMessage() !== '';
  }

  getErrorMessage() {
    return this.errorMessage();
  }

  setErrorMessage(message: string | null) {
    this.errorMessage.set(message);
  }
}
